import type { FuuzeButtonProps, Podcast, PodcastEpisode } from '@fuuze/shared';
import { canUserListenToPodcast, FuuzeButton } from '@fuuze/shared';
import { useMemo } from 'react';
import { IoPauseCircle, IoPlayCircle } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useUser } from '../../Hooks/use-user.hook';
import { usePlayerStore } from '../../store/player.store';

export type PlayerPlayButtonProps = {
  iconSize?: number;
  item: PodcastEpisode;
  channel: Podcast;
  onPlayPauseCallback?: (status: 'play' | 'pause') => void;
} & FuuzeButtonProps;

export function PlayerPlayButton({
  iconSize,
  item,
  channel,
  disabled,
  onPlayPauseCallback,
  ...props
}: PlayerPlayButtonProps) {
  const user = useUser();
  const navigate = useNavigate();
  const [playPause, isPlaying, podcastPlaying, loadAudio, loadError] = usePlayerStore(
    useShallow(state => [state.playPause, state.isPlaying, state.podcastPlaying, state.loadAudio, state.loadError]),
  );

  const isItemThePodcastCurrentlyPlaying = () => item._id === podcastPlaying?.item._id;

  const disableButton = useMemo(() => {
    if (disabled) return true;
    if (loadError && isItemThePodcastCurrentlyPlaying()) return true;
    return false;
  }, [loadError, disabled]);

  const _isPlaying = useMemo(() => {
    return isItemThePodcastCurrentlyPlaying() && isPlaying;
  }, [isPlaying, item, podcastPlaying]);

  const onPlayPause = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    if (!canUserListenToPodcast({ episode: item, podcast: channel, user: user.data })) {
      navigate(`/podcast/${channel?.slug}/tiers?item=${item.slug}`);
      return;
    }

    if (!podcastPlaying || !isItemThePodcastCurrentlyPlaying()) {
      await loadAudio({ channel, item }, { play: true });
    } else {
      await playPause();
    }

    onPlayPauseCallback?.(_isPlaying ? 'pause' : 'play');
  };

  return (
    <FuuzeButton scaleOnHover icon onClick={onPlayPause} disabled={disableButton} {...props}>
      {_isPlaying ? <IoPauseCircle size={iconSize || 48} /> : <IoPlayCircle size={iconSize || 48} />}
    </FuuzeButton>
  );
}
